import { useAuth } from '../lib/auth';
import { t } from '../lib/theme';

export default function Sidebar({ view, setView, stats, pendingEvals, isFellow }) {
  const { profile, signOut, isDirector } = useAuth();
  const isFaculty  = profile?.role === 'faculty';
  const isChairman = profile?.role === 'chairman';

  const fellowNav = [
    { id: 'home',        label: 'Home',         icon: '🏠' },
    { id: 'dashboard',   label: 'Dashboard',    icon: '◈' },
    { id: 'new',         label: 'Log New Case', icon: '⊕' },
    { id: 'activity',    label: 'Add Activity', icon: '📋' },
    { id: 'log',         label: 'My Log',       icon: '≡' },
    { id: 'progress',    label: 'My Progress',  icon: '◉' },
    { id: 'evaluations', label: 'Evaluations',  icon: '★', badge: pendingEvals },
  ];
  const facultyNav = [
    { id: 'home',     label: 'Home',            icon: '🏠' },
    { id: 'faculty',  label: 'Review Queue',    icon: '◈' },
    { id: 'evaluate', label: 'Evaluate Fellow', icon: '★' },
  ];
  const directorNav = [
    { id: 'home',         label: 'Home',              icon: '🏠' },
    { id: 'quiz',         label: "Fellow's Quiz",     icon: '📝' },
    { id: 'director',     label: 'All Fellows',       icon: '◈' },
    { id: 'entries',      label: 'Review Entries',    icon: '✓' },
    { id: 'eval_history', label: 'Evaluations',       icon: '★' },
    { id: 'analytics',    label: 'Program Analytics', icon: '◉' },
    ...(!isChairman ? [{ id: 'evaluate', label: 'Evaluate Fellow', icon: '✎' }] : []),
  ];

  const navItems = (isDirector || isChairman) ? directorNav : isFaculty ? facultyNav : fellowNav;
  const roleLabel = isDirector ? '🎓 Program Director' : isChairman ? '👔 Dept. Chairman' : isFaculty ? '🩺 Faculty' : '👨‍⚕️ Fellow';

  return (
    <div style={s.sidebar}>
      <div style={s.top}>
        <div style={s.logo}>
          <div style={s.mark}>✦</div>
          <div>
            <div style={s.logoText}>Palliative</div>
            <div style={s.logoSub}>Fellowship Log</div>
          </div>
        </div>
        <div style={s.profileCard}>
          <div style={s.avatar}>{profile?.full_name?.[0] || '?'}</div>
          <div style={{ minWidth:0 }}>
            <div style={s.name}>{profile?.full_name || 'Loading…'}</div>
            <div style={s.role}>{roleLabel}</div>
          </div>
        </div>
        <nav style={s.nav}>
          {navItems.map(item => (
            <button key={item.id} onClick={() => setView(item.id)}
              style={{ ...s.navItem, ...(view === item.id ? s.navActive : {}) }}>
              <span style={s.navIcon}>{item.icon}</span>
              <span style={{ flex:1, textAlign:'left' }}>{item.label}</span>
              {item.badge > 0 && (
                <span style={s.badge}>{item.badge}</span>
              )}
            </button>
          ))}
        </nav>
      </div>
      <div style={s.bottom}>
        {isFellow && (
          <div style={s.statsBox}>
            <div style={s.statRow}><span style={s.statLabel}>Total Cases</span><span style={s.statVal}>{stats.total}</span></div>
            <div style={s.statRow}><span style={s.statLabel}>Approved</span><span style={s.statVal}>{stats.approved}</span></div>
            <div style={s.statRow}><span style={s.statLabel}>Pending</span><span style={s.statVal}>{stats.pending}</span></div>
          </div>
        )}
        <button onClick={() => signOut()} style={s.signOut}>⏻ Sign out</button>
      </div>
    </div>
  );
}

const s = {
  sidebar:     { width:232, background:t.slate, display:'flex', flexDirection:'column', minHeight:'100vh', position:'sticky', top:0, height:'100vh', flexShrink:0 },
  top:         { padding:'24px 16px 0', overflowY:'auto', flex:1 },
  logo:        { display:'flex', alignItems:'center', gap:10, marginBottom:20 },
  mark:        { fontSize:20, color:t.clay },
  logoText:    { fontFamily:t.fonts.display, fontSize:16, color:'#F5EFE6', lineHeight:1.1 },
  logoSub:     { fontSize:9, color:'rgba(245,239,230,0.45)', letterSpacing:1.5, textTransform:'uppercase', marginTop:2 },
  profileCard: { background:'rgba(255,255,255,0.07)', borderRadius:10, padding:'10px 12px', marginBottom:18, display:'flex', alignItems:'center', gap:9 },
  avatar:      { width:32, height:32, borderRadius:'50%', background:t.clay, display:'flex', alignItems:'center', justifyContent:'center', fontFamily:t.fonts.display, fontSize:15, color:'#fff', flexShrink:0 },
  name:        { fontSize:12, fontWeight:600, color:'#F5EFE6', lineHeight:1.3, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' },
  role:        { fontSize:10, color:'rgba(245,239,230,0.5)', marginTop:1 },
  nav:         { display:'flex', flexDirection:'column', gap:2 },
  navItem:     { display:'flex', alignItems:'center', gap:10, padding:'10px 13px', borderRadius:8, border:'none', background:'transparent', color:'rgba(245,239,230,0.55)', fontSize:13, fontWeight:500, cursor:'pointer', width:'100%', fontFamily:t.fonts.body, transition:'all 0.15s' },
  navActive:   { background:'rgba(200,149,108,0.18)', color:t.clay, fontWeight:600 },
  navIcon:     { fontSize:13, width:16, flexShrink:0 },
  badge:       { background:t.clay, color:'#fff', borderRadius:20, fontSize:10, padding:'1px 7px', fontWeight:700, marginLeft:'auto' },
  bottom:      { padding:'14px 16px 20px', borderTop:'1px solid rgba(255,255,255,0.07)' },
  statsBox:    { background:'rgba(255,255,255,0.05)', borderRadius:9, padding:'11px 13px', marginBottom:11 },
  statRow:     { display:'flex', justifyContent:'space-between', padding:'4px 0' },
  statLabel:   { fontSize:11, color:'rgba(245,239,230,0.45)' },
  statVal:     { fontSize:13, fontWeight:600, color:t.clay, fontFamily:t.fonts.display },
  signOut:     { width:'100%', padding:'9px', background:'transparent', border:'1px solid rgba(255,255,255,0.1)', borderRadius:8, color:'rgba(245,239,230,0.45)', fontSize:12, cursor:'pointer', fontFamily:t.fonts.body },
};
